import { Box, Button, Container, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRevokeAllTokens } from '@/api/generated/authentication/authentication';
import {
  CardSection,
  ConfirmActionModal,
  PageHeading,
  StatusPill,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableRowStyle
} from '@/components/common';
import { clearSession } from '@/features/auth/context/tokenStorage';

interface KpiItem {
  labelKey: string;
  mobileLabelKey?: string;
  value: string;
  color: string;
}

const kpis: KpiItem[] = [
  { labelKey: 'platformAdmin.kpiOrganizations', value: '24', color: 'var(--color-text-primary)' },
  { labelKey: 'platformAdmin.kpiActiveUsers', value: '186', color: 'var(--color-role-user)' },
  {
    labelKey: 'platformAdmin.kpiSuspendedOrgs',
    mobileLabelKey: 'platformAdmin.kpiSuspendedOrgsMobile',
    value: '2',
    color: 'var(--color-status-warning)'
  },
  {
    labelKey: 'platformAdmin.kpiActiveSessions',
    value: '342',
    color: 'var(--color-status-success)'
  }
];

interface AttentionRow {
  key: string;
  org: string;
  typeKey: string;
  status: 'suspended' | 'active';
  description: string;
}

const attentionRows: AttentionRow[] = [
  {
    key: 'neptun',
    org: 'Neptun Maritime d.o.o.',
    typeKey: 'platformAdmin.typePartner',
    status: 'suspended',
    description: 'Organizacija obustavljena 25.08.2025. - čeka pregled administratora'
  },
  {
    key: 'bluestar',
    org: 'BlueStar Yachts d.o.o.',
    typeKey: 'platformAdmin.typeClient',
    status: 'suspended',
    description: 'Višestruki neuspjeli pokušaji prijave detektirani'
  },
  {
    key: 'dataflow',
    org: 'DataFlow Systems d.o.o.',
    typeKey: 'platformAdmin.typePartner',
    status: 'active',
    description: 'Zahtjev za promjenu tipa organizacije'
  }
];

const activityEvents = [
  { key: 'e1', text: 'Organizacija Neptun Maritime d.o.o. obustavljena', time: 'prije 2 sata' },
  {
    key: 'e2',
    text: 'Korisniku Ana Perić promijenjena uloga u Client Admin',
    time: 'prije 4 sata'
  },
  {
    key: 'e3',
    text: 'Korisnik Ivan Horvat deaktiviran u BlueStar Yachts d.o.o.',
    time: 'jučer 14:32'
  },
  {
    key: 'e4',
    text: 'Sesije opozvane za organizaciju DataFlow Systems d.o.o.',
    time: 'jučer 09:15'
  },
  {
    key: 'e5',
    text: 'Nova organizacija Coastal Engineering d.o.o. registrirana',
    time: '26.08.2025.'
  }
];

const attentionColumns: [string, number, ('center' | undefined)?][] = [
  ['platformAdmin.colOrganization', 220],
  ['platformAdmin.colType', 100],
  ['platformAdmin.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
];

export const AdminDashboardPage = () => {
  const { t } = useTranslation();
  const [revokeAllModalOpen, setRevokeAllModalOpen] = useState(false);

  // onSuccess redirecta odmah, ne čeka da neki idući API poziv organski dobije
  // 401 — pozivatelj je upravo poništio i vlastiti token, pa čekanje na
  // sljedeći (možda tek za nekoliko minuta, ako gleda već keširanu stranicu)
  // ostavlja dojam da je akcija bez učinka.
  const revokeAllMutation = useRevokeAllTokens({
    mutation: {
      onSuccess: () => {
        setRevokeAllModalOpen(false);
        clearSession();
        window.location.href = '/login';
      }
    }
  });

  const revokeAllMessage = (
    <Stack gap={12}>
      <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
        {t('platformAdmin.revokeAllSessionsWillHeading')}
      </Text>
      <Stack gap={4} pl={12}>
        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
          • {t('platformAdmin.revokeAllSessionsWillItem1')}
        </Text>
        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
          • {t('platformAdmin.revokeAllSessionsWillItem2')}
        </Text>
        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
          • {t('platformAdmin.revokeAllSessionsWillItem3')}
        </Text>
      </Stack>
      <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
        {t('platformAdmin.revokeAllSessionsWontHeading')}
      </Text>
      <Stack gap={4} pl={12}>
        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
          • {t('platformAdmin.revokeAllSessionsWontItem1')}
        </Text>
        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
          • {t('platformAdmin.revokeAllSessionsWontItem2')}
        </Text>
      </Stack>
      <Text fz={12.5} fw={500} style={{ color: 'var(--color-status-danger)' }}>
        {t('platformAdmin.revokeAllSessionsWarning')}
      </Text>
    </Stack>
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <PageHeading title={t('platformAdmin.title')} subtitle={t('platformAdmin.subtitle')} />

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={16}>
          {kpis.map((kpi) => (
            <Stack
              key={kpi.labelKey}
              gap={10}
              px={24}
              py={16}
              style={{
                minWidth: 0,
                borderRadius: 12,
                border: '1px solid var(--color-border-soft)',
                backgroundColor: 'var(--color-bg-surface)'
              }}
            >
              <Text
                fz={9}
                fw={600}
                tt="uppercase"
                truncate
                miw={0}
                style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                visibleFrom="sm"
              >
                {t(kpi.labelKey)}
              </Text>
              <Text
                fz={9}
                fw={600}
                tt="uppercase"
                truncate
                miw={0}
                style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                hiddenFrom="sm"
              >
                {t(kpi.mobileLabelKey ?? kpi.labelKey)}
              </Text>
              <Text fz={26} fw={700} style={{ color: kpi.color }}>
                {kpi.value}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>

        <CardSection title={t('platformAdmin.attentionTitle')}>
          <Box style={{ overflowX: 'auto' }} visibleFrom="sm">
            <div style={{ minWidth: 900 }}>
              <div
                className="table-header-row"
                style={{
                  ...tableRowStyle,
                  padding: '12px 20px',
                  backgroundColor: 'var(--color-bg-canvas)',
                  borderBottom: '1px solid var(--color-border-soft)'
                }}
              >
                {attentionColumns.map(([key, width, align]) => (
                  <Text
                    key={key}
                    fz={10}
                    fw={600}
                    w={width}
                    ta={align}
                    style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                  >
                    {t(key)}
                  </Text>
                ))}
                <Text
                  fz={10}
                  fw={600}
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, flex: 1 }}
                >
                  {t('platformAdmin.colDescription')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  w={100}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('platformAdmin.colAction')}
                </Text>
              </div>

              {attentionRows.map((row, index) => (
                <div
                  key={row.key}
                  style={{
                    ...tableRowStyle,
                    padding: '14px 20px',
                    borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                  }}
                >
                  <Text fz={13.5} fw={600} w={220} style={{ color: 'var(--color-text-primary)' }}>
                    {row.org}
                  </Text>
                  <Text fz={13.5} w={100} style={{ color: 'var(--color-text-secondary)' }}>
                    {t(row.typeKey)}
                  </Text>
                  <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                    <StatusPill
                      label={t(
                        row.status === 'suspended'
                          ? 'platformAdmin.statusSuspended'
                          : 'platformAdmin.statusActive'
                      )}
                      bg={
                        row.status === 'suspended'
                          ? 'var(--color-status-danger-soft)'
                          : 'var(--color-status-success-soft)'
                      }
                      fg={
                        row.status === 'suspended'
                          ? 'var(--color-status-danger)'
                          : 'var(--color-status-success)'
                      }
                    />
                  </div>
                  <Text fz={13} style={{ color: 'var(--color-text-secondary)', flex: 1 }}>
                    {row.description}
                  </Text>
                  <Text
                    fz={12}
                    fw={600}
                    w={100}
                    ta="right"
                    style={{
                      color: 'var(--color-accent-primary-active)',
                      textDecoration: 'underline'
                    }}
                  >
                    {t('platformAdmin.detailsLink')}
                  </Text>
                </div>
              ))}
            </div>
          </Box>

          <Stack gap={0} hiddenFrom="sm">
            {attentionRows.map((row, index) => (
              <Stack
                key={row.key}
                gap={8}
                p={14}
                style={{
                  borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                }}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {row.org}
                  </Text>
                  <StatusPill
                    label={t(
                      row.status === 'suspended'
                        ? 'platformAdmin.statusSuspended'
                        : 'platformAdmin.statusActive'
                    )}
                    bg={
                      row.status === 'suspended'
                        ? 'var(--color-status-danger-soft)'
                        : 'var(--color-status-success-soft)'
                    }
                    fg={
                      row.status === 'suspended'
                        ? 'var(--color-status-danger)'
                        : 'var(--color-status-success)'
                    }
                  />
                </Group>
                <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                  {row.description}
                </Text>
                <Text
                  fz={12}
                  fw={600}
                  ta="right"
                  style={{
                    color: 'var(--color-accent-primary-active)',
                    textDecoration: 'underline'
                  }}
                >
                  {t('platformAdmin.detailsLink')}
                </Text>
              </Stack>
            ))}
          </Stack>
        </CardSection>

        <CardSection title={t('platformAdmin.activityTitle')}>
          <Stack gap={16} p={20} visibleFrom="sm">
            {activityEvents.map((event) => (
              <Group key={event.key} gap={8} wrap="wrap">
                <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                  {event.text}
                </Text>
                <Box
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: 'var(--color-accent-primary)'
                  }}
                />
                <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                  {event.time}
                </Text>
              </Group>
            ))}
          </Stack>

          <Stack gap={12} p={14} hiddenFrom="sm">
            {activityEvents.map((event, index) => (
              <Stack
                key={event.key}
                gap={4}
                pt={index === 0 ? undefined : 10}
                style={{
                  borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                }}
              >
                <Text fz={12} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                  {event.text}
                </Text>
                <Group gap={6} wrap="nowrap">
                  <Box
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      backgroundColor: 'var(--color-accent-primary)'
                    }}
                  />
                  <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                    {event.time}
                  </Text>
                </Group>
              </Stack>
            ))}
          </Stack>
        </CardSection>

        <Stack
          gap={12}
          p={20}
          style={{
            width: '100%',
            borderRadius: 12,
            border: '1px solid var(--color-border-default)',
            backgroundColor: 'var(--color-bg-surface)'
          }}
        >
          <Group justify="space-between" wrap="wrap" visibleFrom="sm">
            <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {t('platformAdmin.revokeAllSessionsCardTitle')}
            </Text>
            <Button color="red" size="xs" onClick={() => setRevokeAllModalOpen(true)}>
              {t('platformAdmin.revokeAllSessionsButton')}
            </Button>
          </Group>
          <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }} hiddenFrom="sm">
            {t('platformAdmin.revokeAllSessionsCardTitle')}
          </Text>
          <Text
            fz={{ base: 11.5, sm: 12.5 }}
            lh={{ base: '16px', sm: '18px' }}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {t('platformAdmin.revokeAllSessionsCardDescription')}
          </Text>
          <Box hiddenFrom="sm">
            <Button color="red" size="xs" onClick={() => setRevokeAllModalOpen(true)}>
              {t('platformAdmin.revokeAllSessionsButton')}
            </Button>
          </Box>
        </Stack>
      </Stack>

      <ConfirmActionModal
        opened={revokeAllModalOpen}
        onClose={() => setRevokeAllModalOpen(false)}
        onConfirm={() => revokeAllMutation.mutate({ data: { confirm: 'REVOKE_ALL' } })}
        loading={revokeAllMutation.isPending}
        isPaused={revokeAllMutation.isPaused}
        title={t('platformAdmin.revokeAllSessionsModalTitle')}
        message={revokeAllMessage}
        confirmLabel={t('platformAdmin.revokeAllSessionsConfirm')}
        danger
      />
    </Container>
  );
};
